import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import QRCode from "qrcode";
import { disableTwoFactor, enableTwoFactor, getTwoFactorStatus, startTwoFactorSetup } from "../api/twoFactorApi";
import { getMe } from "../api/authApi";
import { setMe } from "../redux/sessionSlice";
import { useTranslation } from "react-i18next";

const boxCard = { border: "1px solid var(--border, #ccc)", borderRadius: 8, padding: 16, display: "grid", gap: 12, maxWidth: 460 };

/** Two-factor (authenticator app) for the signed-in user: set up, show the recovery codes once, turn off. */
export default function TwoFactorSettings({ forced = false, embedded = false }) {
  const { t } = useTranslation();
  const box = embedded ? { display: "grid", gap: 12 } : boxCard; // embedded: the profile card already draws the frame
  const accessToken = useSelector((s) => s.session?.accessToken);
  const dispatch = useDispatch();
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null); // {secret, otpauth_uri, qr}
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setStatus(await getTwoFactorStatus(accessToken));
  }, [accessToken]);

  useEffect(() => {
    load().catch(() => toast.error(t("profile.twoFactor.statusError")));
  }, [load, t]);

  const refreshMe = async () => dispatch(setMe(await getMe(accessToken)));

  const fail = (error, fallback) => toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || fallback);

  const start = async () => {
    setBusy(true);
    try {
      const data = await startTwoFactorSetup(accessToken);
      setSetup({ ...data, qr: await QRCode.toDataURL(data.otpauth_uri, { margin: 1, width: 192 }) });
    } catch (error) {
      fail(error, t("profile.twoFactor.startError"));
    } finally {
      setBusy(false);
    }
  };

  const enable = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const data = await enableTwoFactor(accessToken, code);
      setRecovery(data.recovery_codes);
      setSetup(null);
      setCode("");
      await load();
      await refreshMe(); // lifts the "setup required" gate
    } catch (error) {
      fail(error, t("profile.twoFactor.codeError"));
    } finally {
      setBusy(false);
    }
  };

  const disable = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await disableTwoFactor(accessToken, password, code);
      toast.success(t("profile.twoFactor.disabledToast"));
      setPassword("");
      setCode("");
      await load();
      await refreshMe();
    } catch (error) {
      fail(error, t("profile.twoFactor.disableError"));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <div style={{ padding: 16 }}>{t("profile.loading")}</div>;

  return (
    <div style={{ display: "grid", gap: 16, padding: 16 }}>
      <h2 style={{ margin: 0 }}>{t("profile.twoFactor.title")}</h2>
      {forced && !status.enabled && (
        <p style={{ margin: 0 }}>
          {t("profile.twoFactor.forced")}
        </p>
      )}

      {recovery && (
        <div style={box} role="alert">
          <strong>{t("profile.twoFactor.recoveryTitle")}</strong>
          <p style={{ margin: 0 }}>{t("profile.twoFactor.recoveryHelp")}</p>
          <pre style={{ margin: 0, userSelect: "all" }}>{recovery.join("\n")}</pre>
          <button type="button" onClick={() => setRecovery(null)}>{t("profile.twoFactor.recoverySaved")}</button>
        </div>
      )}

      {status.enabled ? (
        <form onSubmit={disable} style={box}>
          <span>{t("profile.twoFactor.status")}: <strong>{t("profile.twoFactor.on")}</strong> · {t("profile.twoFactor.codesLeft")}: {status.recovery_codes_left}</span>
          {status.required ? (
            <span>{t("profile.twoFactor.cannotDisable")}</span>
          ) : (
            <>
              <input type="password" placeholder={t("profile.twoFactor.yourPassword")} value={password} onChange={(e) => setPassword(e.target.value)} required />
              <input placeholder={t("profile.twoFactor.code")} value={code} onChange={(e) => setCode(e.target.value)} required autoComplete="one-time-code" />
              <button type="submit" disabled={busy}>{t("profile.twoFactor.disable")}</button>
            </>
          )}
        </form>
      ) : setup ? (
        <form onSubmit={enable} style={box}>
          <span>{t("profile.twoFactor.step1")}</span>
          <img src={setup.qr} alt={t("profile.twoFactor.qrAlt")} width={192} height={192} />
          <span>{t("profile.twoFactor.manualKey")} <code style={{ userSelect: "all" }}>{setup.secret}</code></span>
          <span>{t("profile.twoFactor.step2")}</span>
          <input inputMode="numeric" placeholder="000000" value={code} onChange={(e) => setCode(e.target.value)} required autoComplete="one-time-code" />
          <button type="submit" disabled={busy}>{t("profile.twoFactor.enable")}</button>
        </form>
      ) : (
        <div style={box}>
          <span>{t("profile.twoFactor.status")}: <strong>{t("profile.twoFactor.off")}</strong></span>
          <button type="button" onClick={start} disabled={busy}>{t("profile.twoFactor.startSetup")}</button>
        </div>
      )}
    </div>
  );
}
